import React from 'react';
import { ProcessingMessage as ProcessingMessageType, FileExtractionStatusItem } from 'types/chat.types';
import { ProcessingIndicator } from 'components/molecules/chat';
import Icon from 'components/atoms/Icon';

interface ProcessingMessageProps {
  message: ProcessingMessageType;
}

const statusLabel = (status: FileExtractionStatusItem['status']) => {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'extracting':
      return 'Extracting…';
    case 'failed':
      return 'Failed';
    default:
      return 'Pending';
  }
};

const FileExtractionList: React.FC<{ files: FileExtractionStatusItem[] }> = ({ files }) => (
  <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-3">
    {files.map((file) => (
      <div key={`${file.file_index}-${file.file_name}`} className="flex items-center justify-between gap-2 text-xs">
        <span className="truncate text-gray-700">{file.file_name}</span>
        <span
          className={
            file.status === 'ready'
              ? 'text-teal-700'
              : file.status === 'failed'
                ? 'text-red-600'
                : file.status === 'extracting'
                  ? 'text-blue-600'
                  : 'text-gray-400'
          }
        >
          {statusLabel(file.status)}
        </span>
      </div>
    ))}
  </div>
);

const ProcessingMessage: React.FC<ProcessingMessageProps> = ({ message }) => {
  const { status, progress, queuePosition, timestamp } = message;

  if (progress === 100) {
    return null;
  }

  // NEW: Special handling for verification status
  if (status === 'verification_in_progress') {
    return (
      <div className="self-start flex flex-col">
        <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md max-w-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <Icon name="information-circle" className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Confirm or Update Product Information</p>
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-400 mt-1 ml-2">
          {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    );
  }

  // NEW: Finalizing status
  if (status === 'finalizing' || status === 'verification_completed') {
    return (
      <div className="self-start flex flex-col">
        <div className="bg-white border border-blue-200 px-4 py-3 rounded-2xl rounded-bl-md max-w-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Running Tariff Determination</p>
              <p className="text-xs text-gray-500">Looking up live duty rates from CBP, USITC, and USTR...</p>
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-400 mt-1 ml-2">
          {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    );
  }

  // Default processing display
  const pipelineStage = message.pipelineStage;
  const fileExtractionStatus = message.fileExtractionStatus;
  const displayContent =
    pipelineStage === 'analyzing_files'
      ? message.content || 'Analyzing uploaded files...'
      : message.content;

  return (
    <div className="self-start flex flex-col">
      <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md max-w-2xl">
        <ProcessingIndicator
          status={status}
          progress={progress}
          queuePosition={queuePosition}
          content={displayContent}
        />
        {fileExtractionStatus && fileExtractionStatus.length > 0 && (
          <FileExtractionList files={fileExtractionStatus} />
        )}
      </div>
      <div className="text-xs text-gray-400 mt-1 ml-2">
        {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};

export default ProcessingMessage;
